import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiWithoutCamelization } from "../../../api";
import urls from "../../../urls";
import type { SiteSetupProcessRaw } from "../SiteSetupAssistant.types";

interface UseSiteSetupAssistantMutationsOptions {
  // When false, the mutation's response is NOT written back to the
  // siteSetupAssistant query cache. The default `setQueryData` would change
  // siteSetupAssistantData and trigger the useEffect in SiteSetupAssistant.tsx
  // that reinitialises every form field from the backend response — wiping
  // unsaved user input mid-step. Disable for eager saves where callers
  // selectively write back only the fields they care about.
  updateCache?: boolean;
}

export function useSiteSetupAssistantMutations(
  processId: string | undefined,
  { updateCache = true }: UseSiteSetupAssistantMutationsOptions = {}
) {
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: (newData: SiteSetupProcessRaw) =>
      updateSite(newData, processId),
    onSuccess: updateCache
      ? (data) =>
          queryClient.setQueryData(["siteSetupAssistant", { processId }], data)
      : undefined
  });

  return { updateMutation };
}

async function updateSite(
  newSiteData: SiteSetupProcessRaw,
  processId: string | undefined
) {
  if (!processId) {
    return Promise.reject();
  }

  const response = await apiWithoutCamelization.post<SiteSetupProcessRaw>(
    urls.apiWithoutCamelization.siteSetupAssistant.mieterStromData(processId),
    newSiteData
  );

  return response.data;
}
